import { useEffect, useRef, useState } from "react";
import { Bell, CalendarPlus, Briefcase, Users, X } from "lucide-react";
import { Drawer, DrawerContent, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { IosInstallSteps } from "@/components/IosInstallSteps";
import { detectInAppBrowser, detectPlatform, getDeferredInstallPrompt, isIosSafari, isStandalone } from "@/lib/device";
import { GUEST_POPUP_DELAY, GUEST_POPUP_KEYS, GUEST_POPUP_PAUSE, GUEST_POPUP_VISIT_KEY, guestPopupReady, selectGuestPopup, type GuestPopupKind } from "@/lib/guest-popup";

const BENEFITS = [
  { icon: Bell, text: "Уведомления о событиях твоего города и по твоим интересам" },
  { icon: Users, text: "Отмечай, куда идёшь, и находи своих" },
  { icon: CalendarPlus, text: "Размещай свои события" },
  { icon: Briefcase, text: "Размещайся в каталоге экспертов" },
];

export function CommunityBenefits({ compact }: { compact?: boolean }) {
  return (
    <ul className={`flex flex-col ${compact ? "gap-1.5" : "gap-2.5"}`}>
      {BENEFITS.map(({ icon: Icon, text }) => (
        <li key={text} className={`flex items-start gap-2 text-td-dark ${compact ? "text-[13px]" : "text-sm"}`}>
          <Icon className="mt-0.5 h-4 w-4 shrink-0 text-td-teal" aria-hidden />
          <span>{text}</span>
        </li>
      ))}
    </ul>
  );
}

export function GuestJoinSheet({
  open,
  onOpenChange,
  onSignup,
  onLogin,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSignup: () => void;
  onLogin: () => void;
}) {
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="px-5 pb-6">
        <button
          type="button"
          aria-label="Закрыть"
          onClick={() => onOpenChange(false)}
          className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full text-td-muted hover:bg-td-soft"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="mx-auto w-full max-w-md pt-4">
          <DrawerTitle className="pr-10 font-serif text-[22px] font-semibold text-td-dark">
            Это доступно участникам сообщества
          </DrawerTitle>
          <DrawerDescription className="mt-1 text-sm text-td-muted">
            Зарегистрируйся: минута, бесплатно
          </DrawerDescription>
          <div className="mt-4">
            <CommunityBenefits />
          </div>
          <button
            type="button"
            onClick={onSignup}
            className="mt-6 flex h-12 w-full items-center justify-center rounded-full bg-td-orange text-base font-semibold text-white hover:bg-td-orange/90"
          >
            Зарегистрироваться
          </button>
          <button
            type="button"
            onClick={onLogin}
            className="mt-2 flex h-11 w-full items-center justify-center text-sm font-semibold text-td-teal"
          >
            Уже есть профиль? Войти
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

export function GuestPopup({ allTab, eventId, filtersOpen, joinOpen, onSignup, onLogin }: {
  allTab: boolean;
  eventId: string | null;
  filtersOpen: boolean;
  joinOpen: boolean;
  onSignup: () => void;
  onLogin: () => void;
}) {
  const [kind, setKind] = useState<GuestPopupKind | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [openedCards, setOpenedCards] = useState(0);
  const [step, setStep] = useState<"intro" | "ios" | "android">("intro");
  const previousEvent = useRef<string | null>(null);
  const shown = useRef(false);
  const blocked = !allTab || !!eventId || filtersOpen || joinOpen;

  useEffect(() => {
    const timer = window.setTimeout(() => setElapsed(GUEST_POPUP_DELAY), GUEST_POPUP_DELAY);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (eventId && eventId !== previousEvent.current) setOpenedCards((count) => count + 1);
    previousEvent.current = eventId;
  }, [eventId]);

  useEffect(() => {
    if (shown.current || !guestPopupReady(elapsed, openedCards, blocked)) return;
    try {
      // Проверяем чтение и запись обоих хранилищ до показа окна.
      const probe = "td_guest_popup_storage_probe";
      for (const storage of [window.localStorage, window.sessionStorage]) {
        storage.setItem(probe, "1");
        if (storage.getItem(probe) !== "1") return;
        storage.removeItem(probe);
      }
      const next = selectGuestPopup({
        now: Date.now(),
        skipInstall: isStandalone() || detectPlatform() === "desktop" || !!detectInAppBrowser() || (detectPlatform() === "ios" && !isIosSafari()),
        installUntil: Number(localStorage.getItem(GUEST_POPUP_KEYS.install) || 0),
        signupUntil: Number(localStorage.getItem(GUEST_POPUP_KEYS.signup) || 0),
        shown: sessionStorage.getItem(GUEST_POPUP_VISIT_KEY) === "1",
        storageAvailable: true,
      });
      if (!next) return;
      sessionStorage.setItem(GUEST_POPUP_VISIT_KEY, "1");
      shown.current = true;
      setKind(next);
    } catch {
      // Без доступного хранилища окно не показываем.
    }
  }, [elapsed, openedCards, blocked]);

  const close = (pause: boolean) => {
    if (pause && kind) {
      try {
        localStorage.setItem(GUEST_POPUP_KEYS[kind], String(Date.now() + GUEST_POPUP_PAUSE));
      } catch {
        // Окно остаётся закрытым при недоступном хранилище.
      }
    }
    setKind(null);
  };
  const platform = kind ? detectPlatform() : "desktop";
  const install = async () => {
    if (platform === "ios") { setStep("ios"); return; }
    const prompt = getDeferredInstallPrompt();
    if (!prompt) { setStep("android"); return; }
    try {
      await prompt.prompt();
      const choice = await prompt.userChoice.catch(() => null);
      if (choice?.outcome === "accepted") close(false);
      else setStep("android");
    } catch {
      setStep("android");
    }
  };
  const title = kind === "signup"
    ? "Новые события города по твоим интересам"
    : step === "ios" ? "Два касания, и я у тебя на экране"
    : "Посели афишу у себя в телефоне 🐾";
  const primaryClass = "mt-6 min-h-12 h-auto w-full whitespace-normal rounded-full bg-td-orange px-4 py-3 text-base font-semibold text-primary-foreground hover:bg-td-orange/90";

  return (
    <Drawer open={!!kind && !blocked} onOpenChange={(open) => { if (!open) close(true); }}>
      <DrawerContent className="max-h-[90dvh] overflow-y-auto px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
        <Button variant="ghost" size="icon" aria-label="Закрыть" onClick={() => close(true)} className="absolute right-3 top-3 h-10 w-10 rounded-full text-td-muted hover:bg-td-soft"><X /></Button>
        <div className="mx-auto w-full max-w-md pt-4">
          <DrawerTitle className="pr-10 font-serif text-[22px] font-semibold text-td-dark">{title}</DrawerTitle>
          <DrawerDescription className={step === "ios" ? "sr-only" : "mt-3 text-base text-td-muted"}>
            {kind === "signup"
              ? "Создай профиль за минуту: выбери город и темы, и я буду сообщать о новых событиях, которые тебе подойдут. Заодно откроются сообщества, знакомства и каталог экспертов."
              : "Привет, я MrDrive! Собрал для тебя события твоего города в одном месте. Добавь TurkeyDrive на главный экран, и не придётся каждый раз искать ссылку."}
          </DrawerDescription>
          {kind === "signup" ? <>
            <Button className={primaryClass} onClick={onSignup}>Зарегистрироваться</Button>
            <p className="mt-2 text-center text-xs text-td-muted">Бесплатно, одна минута</p>
            <Button variant="link" className="mt-2 h-auto min-h-11 w-full whitespace-normal text-td-teal" onClick={onLogin}>Уже есть профиль? Войти</Button>
          </> : step === "ios" ? <>
            <IosInstallSteps className="mt-5 text-base text-td-dark" />
            <Button className={primaryClass} onClick={() => close(false)}>Понятно</Button>
          </> : <>
            {step === "android" && <p className="mt-5 text-base text-td-dark">Открой меню ⋮ в браузере → «Установить приложение»</p>}
            {step === "intro" && <Button className={primaryClass} onClick={() => void install()}>Добавить на главный экран</Button>}
          </>}
          <Button variant="link" className="mt-2 min-h-11 w-full text-sm text-td-muted" onClick={() => close(true)}>{kind === "signup" ? "Вернуться к событиям" : "Пока осмотрюсь"}</Button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
